import { useEffect, useId, useState } from "react";
import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";

const beliefItems = [
  "AI implementation must preserve policy, evidence, and human accountability.",
  "CMMC readiness has to live in the operating environment, not only in documents.",
  "Security evidence should be traceable enough for operators, assessors, and executives.",
  "Machine-speed operations require policy-bound systems, not slower meetings."
];

const operatingPrinciples = [
  {
    icon: "shield" as const,
    title: "Regulated discipline",
    copy: "We build for organizations that need security controls, evidence, and operational rigor to hold up under scrutiny."
  },
  {
    icon: "code" as const,
    title: "Implementation bias",
    copy: "Our work is grounded in deployable environments, automation, remediation, and production constraints."
  },
  {
    icon: "document" as const,
    title: "Evidence by design",
    copy: "Proof belongs inside the operating model, not in a spreadsheet assembled after work is complete."
  }
];

type TeamMember = {
  bio: string[];
  imageAlt?: string;
  imageSrc?: string;
  name: string;
  summary: string;
  title: string;
};

type TeamSection = {
  copy: string;
  members: TeamMember[];
  title: string;
};

const teamSections: TeamSection[] = [
  {
    title: "Board",
    copy: "Company leadership responsible for strategy, technology, operations, and executive coordination.",
    members: [
      {
        name: "Anthony Maisonet",
        title: "Co-Founder & CEO",
        imageSrc: "/assets/img/team/anthony-maisonet.jpg",
        imageAlt: "Anthony Maisonet headshot",
        summary: "Leads Holocron's mission, market strategy, partnerships, and long-term customer success.",
        bio: [
          "Anthony Maisonet is the Co-Founder and CEO of Holocron Security, where he leads the company's mission to modernize cybersecurity through innovative technologies that empower organizations to operate more securely, efficiently, and intelligently. As an entrepreneur and business leader, Anthony is focused on building scalable solutions that address real-world security challenges across both commercial and government sectors.",
          "Prior to founding Holocron, Anthony successfully scaled and led multiple businesses supporting critical infrastructure organizations throughout the Pacific region, gaining extensive experience in operations, technology, and organizational growth. His background in building and growing companies has shaped his leadership approach at Holocron, where he is committed to fostering innovation, execution, and long-term customer success.",
          "Anthony is currently pursuing his Doctorate in Business Administration at Pepperdine University, where he continues to deepen his expertise in leadership, strategy, and organizational development. He is passionate about building high-performing teams, transforming emerging technologies into impactful products, and driving the next generation of cybersecurity innovation."
        ]
      },
      {
        name: "Craig Opie",
        title: "Co-Founder & CTO",
        imageSrc: "/assets/img/team/craig-opie.jpg",
        imageAlt: "Craig Opie headshot",
        summary: "Leads Holocron's technical architecture across secure AI, infrastructure, automation, and evidence systems.",
        bio: [
          "Craig Opie leads Holocron's technical architecture across secure AI implementation, regulated infrastructure, automation, and evidence systems. His work focuses on building technology that is secure, deployable, and useful for organizations operating in high-trust environments."
        ]
      },
      {
        name: "Manny Abello",
        title: "Co-Founder & COO",
        imageSrc: "/assets/img/team/manny-abello.jpg",
        imageAlt: "Manny Abello headshot",
        summary: "Leads operational delivery, customer execution, and the disciplined processes required for regulated environments.",
        bio: [
          "Manny Abello leads operational delivery, customer execution, and the disciplined processes required for regulated environments. He helps translate Holocron's strategy into repeatable delivery motions for customers, partners, and internal teams."
        ]
      },
      {
        name: "Malia Novak",
        title: "Board Executive Assistant",
        imageSrc: "/assets/img/team/malia-novak.jpg",
        imageAlt: "Malia Novak headshot",
        summary: "Partners with leadership to keep the company organized, coordinated, and moving across priorities.",
        bio: [
          "Malia Novak is an Executive Assistant at Holocron Security, partnering with leadership to keep things organized, moving, and connected across the company. Originally from Hawaii's Big Island and a graduate of Florida State University, she thrives in fast-paced environments and enjoys helping teams do their best work. Outside of work, she loves traveling, exploring new food scenes, practicing yoga, and embracing opportunities to experience new places and cultures."
        ]
      }
    ]
  },
  {
    title: "Engineering & Operations",
    copy: "Technical and operational leads building the systems, workflows, and infrastructure behind Holocron's products.",
    members: [
      {
        name: "Roberto Delgado",
        title: "Platform & Analytics Lead",
        imageSrc: "/assets/img/team/roberto.jpg",
        imageAlt: "Roberto Delgado headshot",
        summary: "Builds and evolves web platforms, analytics systems, and workflow-connected digital infrastructure.",
        bio: [
          "Roberto Delgado has spent more than a decade building and evolving web platforms, analytics systems, and digital infrastructure for all kinds of organizations, from national nonprofits to commercial enterprises.",
          "His work spans analytics engineering, data instrumentation, and platform development, helping teams turn complicated data into something they can actually use. Most recently, he led the rollout of an internal AI agent that plugs directly into business workflows. He loves working where software, data, and emerging tech all overlap, building products that are technically solid and genuinely useful to the people who depend on them.",
          "When he's not working, you'll probably find him camping or out hiking the trails of Oahu with his kids. He's also an avid reader and writes poetry whenever the mood strikes."
        ]
      },
      {
        name: "Cameron Krischel",
        title: "Software Development Lead",
        imageSrc: "/assets/img/team/cameron-krischel.jpg",
        imageAlt: "Cameron Krischel headshot",
        summary: "Heads engineering for Rust applications, agentic AI workflows, and CI/CD-backed delivery.",
        bio: [
          "Cameron Krischel is Software Development Lead at Holocron Security, where he heads engineering for the company's Rust applications and the agentic AI workflows and multi-agent systems built for regulated, compliance-driven environments, all delivered through fully integrated GitLab CI/CD pipelines. He describes himself simply as a problem solver, a thread that runs through years of building, securing, and modernizing software in high-stakes settings.",
          "Cameron's career has been spent engineering software inside U.S. defense programs. At BAE Systems, he worked on web properties and authentication services for the DoD's High Performance Computing Modernization Program, supporting OIDC and SSO across all five DoD Supercomputing Resource Centers and helping modernize delivery practices with version control, containerized deployments, and CI/CD-driven security scanning. He began his career as a software development analyst at Northrop Grumman. He holds active Top Secret and Secret clearances."
        ]
      },
      {
        name: "Wes Pritchard",
        title: "Global ITSM & Infrastructure Lead",
        imageSrc: "/assets/img/team/wes-pritchard.jpg",
        imageAlt: "Wes Pritchard headshot",
        summary: "Leads ITSM and infrastructure with a focus on stable, digestible architecture and smarter operations.",
        bio: [
          "Wes Pritchard is the Global ITSM & Infrastructure Lead for Holocron Security. Before that, he spent almost 12 years in the U.S. Navy and 4 years as a government IT contractor. In each of his work environments, Wes has quickly risen through the ranks thanks to his curiosity, quick learning, and attention to detail. He values stability, clear and digestible architectural design, and a bias toward working smarter, not harder.",
          "Outside of work, Wes runs a large homelab where he self-hosts several services for himself and his family, and spins up and down new technologies to learn and break things in a safe place. He also enjoys video games and lifting weights in his free time."
        ]
      }
    ]
  },
  {
    title: "Growth",
    copy: "Client-facing team members helping organizations understand security posture, compliance needs, and practical next steps.",
    members: [
      {
        name: "Danielle Wilson",
        title: "Account Executive",
        imageSrc: "/assets/img/team/danielle-wilson.jpg",
        imageAlt: "Danielle Wilson headshot",
        summary: "Supports clients by connecting security posture, compliance challenges, and business needs to practical solutions.",
        bio: [
          "Danielle Wilson is an Account Executive at Holocron, holding a Bachelor of Science in Computer Technology with a concentration in Cloud Computing and a minor in Marketing from American Public University. She also has certifications in Microsoft Azure, Databricks Lakehouse Fundamentals, and Databricks Generative AI Fundamentals. At Holocron, Danielle supports clients by helping them better understand their security posture, compliance challenges, and technology needs while connecting them with solutions that make sense for their business.",
          "Danielle brings a strong blend of client-centered service, relationship building, and technical knowledge to her role. With a background in health, wellness, and client success, Danielle has spent years helping people understand their needs, make confident decisions, and feel supported throughout the process. Her experience in sales, service, and account management gives her a practical, people-first approach to working with customers and building long-term trust. Based in San Diego, she is passionate about being a reliable point of contact and trusted advisor for the organizations she serves."
        ]
      },
      {
        name: "Art Agushi",
        title: "Account Executive",
        imageSrc: "/assets/img/team/art-agushi.jpg",
        imageAlt: "Art Agushi headshot",
        summary: "Brings cybersecurity, critical infrastructure, and emerging technology experience to customer conversations.",
        bio: [
          "Originally from New York and of Kosovar heritage, Art Agushi has built his career across cybersecurity, critical infrastructure, and emerging technologies. His experience spans securing oil and gas drilling operations, supporting industrial IT and OT environments, and managing large-scale data center and cryptocurrency mining infrastructure. When he's not helping organizations strengthen their security posture and navigate complex compliance challenges, you'll likely find him working on music, exploring new technologies, or connecting with his roots in Kosovo."
        ]
      },
      {
        name: "Rhett Smith",
        title: "Account Executive",
        imageSrc: "/assets/img/team/rhett-smith.jpg",
        imageAlt: "Rhett Smith headshot",
        summary: "Combines military service, logistics, operations, and technology learning in a customer-focused role.",
        bio: [
          "Rhett Smith is a U.S. Navy veteran currently pursuing a Bachelor of Science in Information Technology at Florida State College at Jacksonville. His background includes military service, the Merchant Marine, logistics, customer service, and operations, all of which have strengthened his leadership, teamwork, and problem-solving skills. He enjoys learning new technologies and continues building his knowledge in networking, cybersecurity, data analytics, and software development.",
          "Outside of school, Rhett enjoys staying active, working out, and continually improving different areas of his life. Whether learning a new technical skill, improving his home, or refining his personal style, he believes in setting goals and consistently working toward them. Family is also a major motivation for him, and becoming a father has strengthened his desire to build a stable and successful future. He takes pride in being dependable, disciplined, and willing to learn."
        ]
      },
      {
        name: "Darryan Wells",
        title: "Account Executive",
        imageSrc: "/assets/img/team/growth-account-executive.jpg",
        imageAlt: "Darryan Wells headshot",
        summary: "Builds trusted customer relationships through disciplined communication, negotiation, and mission-first service.",
        bio: [
          "Darryan Wells is driven by a passion for building trust, solving complex challenges, and helping organizations achieve meaningful results. His real estate and military background has helped develop strong communication, negotiation, and client relationship skills while working in fast-paced, customer-focused environments.",
          "As a military veteran, Darryan brings discipline, adaptability, and a mission-first mindset to every opportunity. He is passionate about continuous learning and thrives in environments where collaboration, innovation, and strategic thinking drive success. At Holocron Security, he helps organizations strengthen their security posture while contributing to the company's continued growth."
        ]
      },
      {
        name: "Kenny Ladler",
        title: "Account Executive",
        imageSrc: "/assets/img/team/kenny.jpg",
        imageAlt: "Kenny Ladler headshot",
        summary: "Former-NFL Pro who brings a strategic, relationship-focused approach to helping customers succeed.",
        bio: [
          "Originally from Stone Mountain, Georgia, Kenny Ladler is a Former-NFL Pro and Account Executive with a passion for building strong relationships and helping customers succeed. A Vanderbilt University graduate, he brings a thoughtful, strategic approach to his work. Outside the office, you'll likely find him playing chess, always enjoying a good challenge and the opportunity to think a few moves ahead."
        ]
      },
      {
        name: "Christopher Hattar",
        title: "Account Executive",
        imageSrc: "/assets/img/team/christopher-hattar.jpg",
        imageAlt: "Christopher Hattar headshot",
        summary: "Brings retail leadership, customer service, team development, and operational excellence to client relationships.",
        bio: [
          "Christopher Hattar is an experienced retail leader with more than seven years of management experience in sales, customer service, and team development. He is passionate about creating exceptional customer experiences, coaching high-performing teams, and driving operational excellence through collaboration and accountability.",
          "Christopher takes pride in building strong relationships with both customers and colleagues and believes that great leadership starts with integrity, communication, and a commitment to continuous improvement. He enjoys taking on new challenges and is always looking for opportunities to grow professionally while making a positive impact on the people around him.",
          "Outside of work, Christopher enjoys traveling, following sports, exploring new technology, and spending time with his wife."
        ]
      }
    ]
  }
];

function getInitials(name: string) {
  return name
    .split(" ")
    .filter((part) => part && part.toLowerCase() !== "pending")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function AboutPage() {
  const [activeBio, setActiveBio] = useState<TeamMember | null>(null);
  const bioDialogTitleId = useId();

  useEffect(() => {
    if (!activeBio || typeof document === "undefined") {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveBio(null);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeBio]);

  useDocumentMeta({
    canonicalPath: "/who-we-are",
    description:
      "Holocron Security builds Conscious Security capabilities for secure AI implementation, CMMC environments, and regulated operations.",
    title: "Who We Are | Holocron Security"
  });

  return (
    <>
      <PageHero
        actions={[
          { href: contactHref, label: "Work With Holocron", variant: "primary" },
          { href: "/solutions", label: "Explore Solutions", variant: "secondary" }
        ]}
        copy="Holocron Security exists to help regulated organizations deploy AI and infrastructure under machine-speed, policy-bound defense and operations."
        eyebrow="Company"
        title="Builders of Conscious Security."
      />

      <section className="surface-light">
        <div className="section-shell story-grid">
          <div>
            <p className="section-kicker section-kicker--light">Our mission</p>
            <h2 className="section-title section-title--light">
              Make secure AI practical for regulated teams.
            </h2>
          </div>
          <div className="story-copy story-copy--light">
            <p>
              Holocron combines security engineering, compliance discipline, and implementation services so DIB and
              regulated organizations can move faster without losing control of risk, evidence, or authorization.
            </p>
            <ul className="bullet-list">
              {beliefItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="surface-dark">
        <div className="section-shell">
          <div className="section-intro">
            <p className="section-kicker">How we work</p>
            <h2 className="section-title">Operator credibility for regulated work.</h2>
          </div>
          <div className="use-case-grid">
            {operatingPrinciples.map((card) => (
              <article key={card.title} className="use-case-card">
                <div className="icon-badge icon-badge--glass">
                  <Icon name={card.icon} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-purple-glow team-section">
        <div className="section-shell">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">Our Team</p>
              <h2 className="section-title">Built by operators, engineers, and regulated-market leaders.</h2>
            </div>
            <SmartLink className="button secondary" href={contactHref}>
              Start a Conversation
            </SmartLink>
          </div>
          <div className="team-section-stack">
            {teamSections.map((section) => (
              <div key={section.title} className="team-section-block">
                <div className="team-section-header">
                  <div>
                    <p className="section-kicker">{section.title}</p>
                    <p>{section.copy}</p>
                  </div>
                </div>
                <div className="team-grid team-grid--people">
                  {section.members.map((member) => (
                    <article key={`${section.title}-${member.name}`} className="team-card team-card--person">
                      <div className={member.imageSrc ? "team-avatar team-avatar--photo" : "team-avatar team-avatar--placeholder"}>
                        {member.imageSrc ? (
                          <img alt={member.imageAlt ?? `${member.name} headshot`} src={member.imageSrc} />
                        ) : (
                          <span>{getInitials(member.name) || "HS"}</span>
                        )}
                      </div>
                      <div className="team-card-body">
                        <h3>{member.name}</h3>
                        <p className="team-role">{member.title}</p>
                        <p className="team-summary">{member.summary}</p>
                        <button className="team-bio-trigger" onClick={() => setActiveBio(member)} type="button">
                          <span>Read full bio</span>
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {activeBio ? (
        <div className="team-bio-bubble-backdrop" onClick={() => setActiveBio(null)} role="presentation">
          <aside
            aria-labelledby={bioDialogTitleId}
            aria-modal="true"
            className="team-bio-bubble"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
          >
            <div className="team-bio-bubble-header">
              <div className={activeBio.imageSrc ? "team-bio-bubble-avatar" : "team-bio-bubble-avatar team-bio-bubble-avatar--placeholder"}>
                {activeBio.imageSrc ? (
                  <img alt="" src={activeBio.imageSrc} />
                ) : (
                  <span>{getInitials(activeBio.name) || "HS"}</span>
                )}
              </div>
              <div>
                <p className="section-kicker">Full Bio</p>
                <h2 id={bioDialogTitleId}>{activeBio.name}</h2>
                <p>{activeBio.title}</p>
              </div>
              <button
                aria-label={`Close ${activeBio.name} bio`}
                className="team-bio-close"
                onClick={() => setActiveBio(null)}
                type="button"
              >
                <Icon name="x-circle" />
              </button>
            </div>
            <div className="team-bio-bubble-copy">
              {activeBio.bio.map((paragraph, index) => (
                <p key={`${activeBio.name}-bubble-bio-${index}`}>{paragraph}</p>
              ))}
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}
